
import { Component, Vue } from "vue-property-decorator";

import ListRequest, { Filter, Ordering, OrderType } from "@/models/ListRequest";
import Coupon, {
  DiscountType,
  CouponType,
} from "@/models/Coupon/Coupon";
import CreateCoupon from "@/models/Coupon/CreateCoupon";
import UpdateCoupon from "@/models/Coupon/UpdateCoupon";
import { mapState } from "vuex";
import CouponsRepository from "@/utils/API/CouponsRepository";
import { TRANSLATIONS_FETCH, PARTNERS_REDIRECT } from "@/store/ActionTypes";
import Partner from "@/models/Partner/Partner";
import PartnersRepository from "@/utils/API/PartnersRepository";
import { dateDisplayFormat, dateTimeDisplayFormat } from "@/utils/Defaults";

import vTable from "@/components/vTable.vue";
import vModal from "@/components/vModal.vue";
import vToggleSwitch from "@/components/vToggleSwitch.vue";

interface CouponsState {
  /**
   * Number of packages displayed on the grid
   */
  count: number;
  /**
   * Total entries in the grid
   */
  totalCount: number;
  /**
   * List of coupons in the system determined by page and page size
   */
  coupons: Coupon[];
  /**
   * Current page on the grid
   */
  page: number;
  /**
   * Maximum visible rows in the grid
   */
  pageSize: number;
  /**
   * List of filters
   */
  filters: Filter[];
  /**
   * Ordering
   */
  orders: Ordering;

  /**
   * Model for coupon creation
   */
  createCoupon: CreateCoupon;
  /**
   * Model for coupon edit
   */
  updateCoupon: UpdateCoupon;
  /**
   * List of coupon ids to be removed
   */
  removeList: number[];

  /**
   * List of partners for creating partner coupon
   */
  partners: Partner[];

  /**
   * Chosen partner for creating partner coupon
   */
  partner: Partner;

  /**
   * Is the grid loading?
   */
  tableLoading: boolean;
}

@Component({
  components: {
    vTable,
    vModal,
    vToggleSwitch,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule
    })
  }
})
class Coupons extends Vue {
  localizationState: any;

  couponsState: CouponsState = {
    count: 0,
    totalCount: 0,
    coupons: [],
    page: 1,
    pageSize: 25,
    filters: [],
    orders: { field: "", orderType: OrderType.Asc },
    createCoupon: {
      partnerId: 0,
      name: "",
      usageLimit: 0,
      expiryDate: null,
      value: 0,
      discountType: DiscountType.Absolute,
      couponType: CouponType.Individual
    },
    updateCoupon: {
      id: 0,
      name: "",
      discountType: DiscountType.Absolute,
      couponType: CouponType.Individual,
      isActive: false,
      partnerId: 0,
      usageLimit: 0,
      expiryDate: null,
      value: 0
    },
    removeList: [],

    partners: [],
    partner: {
      id: 0,
      name: "",
      address: "",
      phone: "",
      email: "",
      couponsCount: 0
    },

    tableLoading: false
  };

  // Properties for vTable grid
  selectErase = true;
  searchable = true;
  editableTable = true;

  // Modal properties
  showModal = false;
  modalTitle = "";
  showRemovePrompt = false;
  removePromptTitle = "";

  editMode = false;

  // Datepicker option object to disable dates before today
  disabledDates = {
    to: new Date()
  };

  /**
   * Method used by the grid after catching the 'changePage'
   * event from vTable
   *
   * @param {any} args
   */
  changePage(args: any) {
    const orderings: Ordering[] = [];
    orderings.push({ ...args[3] });

    this.requestCoupons(
      new ListRequest({
        page: args[0],
        pageSize: this.couponsState.pageSize,
        filters: args[2],
        orders: orderings
      })
    );
    this.couponsState.page = args[0];
    this.couponsState.orders = { ...args[3] };
    this.couponsState.filters = [...args[2]];
  }

  /**
   * Method used by the grid after catching the 'search'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  search(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });

    this.requestCoupons(
      new ListRequest({
        page: 1,
        pageSize: this.couponsState.pageSize,
        filters: filters,
        orders: orderings
      })
    );

    this.couponsState.page = 1;
    this.couponsState.orders = { ...ordering };
    this.couponsState.filters = [...filters];
  }

  /**
   * Method used by the grid after catching the 'order'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  order(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });

    this.requestCoupons(
      new ListRequest({
        page: this.couponsState.page,
        pageSize: this.couponsState.pageSize,
        filters: filters,
        orders: orderings
      })
    );

    this.couponsState.orders = { ...ordering };
    this.couponsState.filters = [...filters];
  }

  /**
   * Method that resets most variables and closes all modals.
   */
  resetState() {
    this.showModal = false;
    this.editMode = false;
    this.modalTitle = this.translations.get("coupons_modal_title_add");

    this.showRemovePrompt = false;
    this.removePromptTitle = "";

    this.couponsState.updateCoupon = {
      id: 0,
      name: "",
      discountType: DiscountType.Absolute,
      couponType: CouponType.Individual,
      isActive: false,
      partnerId: 0,
      usageLimit: 0,
      expiryDate: null,
      value: 0
    };

    this.couponsState.createCoupon = {
      partnerId: 0,
      name: "",
      usageLimit: 0,
      expiryDate: null,
      value: 0,
      discountType: DiscountType.Absolute,
      couponType: CouponType.Individual
    };

    this.couponsState.partner = {
      id: 0,
      name: "",
      address: "",
      phone: "",
      email: "",
      couponsCount: 0
    };
  }

  /**
   * Method for edit / add package modal toggling
   */
  toggleModal() {
    this.showModal = !this.showModal;
  }

  /**
   * Getter for grid headers. Done this way because headers are translatable and async
   */
  get tableHeaders() {
    return [
      {
        name: this.translations.get("coupons_table_header_name"),
        key: "Name",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons_table_header_code"),
        key: "Code",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons_table_header_partner"),
        key: "Partner",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons_table_header_created_date"),
        key: "CreatedOn",
        searchable: false,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons_table_header_status"),
        key: "IsActive",
        searchable: false,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons_table_header_type"),
        key: "CouponType",
        searchable: false,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons_table_header_expiry_date"),
        key: "ExpiryDate",
        searchable: false,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons_table_header_discount_type"),
        key: "DiscountType",
        searchable: false,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons_table_header_value"),
        key: "Value",
        searchable: false,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons_table_header_limit"),
        key: "UsageLimit",
        searchable: false,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("coupons_table_used_coupons"),
        key: "NumberOfUsages",
        searchable: false,
        searchString: "",
        sortable: true
      }
    ];
  }

  /**
   * Setting modal title for Adding new coupon and toggling the modal
   */
  prepareAddMode() {
    this.modalTitle = this.translations.get("coupons_modal_title_add");

    this.toggleModal();
  }

  /**
   * Populating form and toggling the modal for editing coupon.
   * Sets the edit mode to true and toggling the modal
   *
   * @param {number} id Id of the package
   */
  prepareEditMode(id: number) {
    this.editMode = true;
    this.modalTitle = this.translations.get("coupons_modal_title_edit");

    let found = this.couponsState.coupons.find((c) => c.id === id);

    if (typeof found !== "undefined") {
      let temp: UpdateCoupon = {
        id: found.id,
        name: found.name,
        discountType: found.discountType,
        couponType: found.couponType,
        isActive: found.isActive,
        partnerId: found.partnerId,
        usageLimit: found.usageLimit,
        expiryDate: found.expiryDate,
        value: found.value
      };

      this.couponsState.updateCoupon = { ...temp };
      this.couponsState.partner = { ...found.partner };

      this.toggleModal();
    }
  }

  /**
   * Method for displaying prompt message for selected coupons to be deleted
   *
   * @param {number[]} ids Package ids
   */
  prepareDeletePrompt(ids: number[]) {
    this.couponsState.removeList = ids;
    this.removePromptTitle = this.translations.get(
      "coupons_delete_prompt_message",
      ids.length
    );
    this.showRemovePrompt = true;
  }

  /**
   * Method for deleting selected coupons and refreshing the grid.
   */
  confirmDelete() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get(
        "coupons_toastr_deleting",
        this.couponsState.removeList.length
      ),
      () =>
        new Promise((resolve, reject) => {
          CouponsRepository.deleteCoupons({
            coupons: this.couponsState.removeList
          })
            .then(() => {
              resolve({
                body: this.translations.get(
                  "coupons_toastr_deleted",
                  this.couponsState.removeList.length
                )
              });

              this.couponsState.page = 1;

              this.requestCoupons(
                new ListRequest({
                  page: this.couponsState.page,
                  pageSize: this.couponsState.pageSize,
                  filters: this.couponsState.filters,
                  orders: [this.couponsState.orders]
                })
              );
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true
      }
    );
    this.resetState();
  }

  /**
   * Router method for submitting to API
   */
  submit() {
    this.$validator.validateAll().then((result) => {
      if (result) {
        if (this.editMode) {
          this.submitUpdate();
        }
        else {
          this.submitNew();
        }
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  /**
   * Method for submitting edit to api and refreshing the grid
   */
  submitUpdate() {
    let coupon = this.couponsState.coupons.find(
      (coupon) => coupon.id === this.couponsState.updateCoupon.id
    );

    if (
      typeof coupon !== "undefined" &&
      (coupon.name !== this.couponsState.updateCoupon.name ||
        coupon.discountType !== this.couponsState.updateCoupon.discountType ||
        coupon.couponType !== this.couponsState.updateCoupon.couponType ||
        coupon.isActive !== this.couponsState.updateCoupon.isActive ||
        coupon.partnerId !== this.couponsState.partner.id ||
        coupon.usageLimit !== this.couponsState.updateCoupon.usageLimit ||
        coupon.expiryDate !== this.couponsState.updateCoupon.expiryDate ||
        coupon.value !== this.couponsState.updateCoupon.value)
    ) {
      if (this.couponsState.updateCoupon.couponType === CouponType.Partner) {
        this.couponsState.updateCoupon.expiryDate = null;
        this.couponsState.updateCoupon.partnerId = this.couponsState.partner.id;
      }
      else {
        this.couponsState.updateCoupon.partnerId = null;
      }
      // @ts-ignore
      this.$snotify.async(
        this.translations.get("coupons_toastr_updating"),
        () =>
          new Promise((resolve, reject) => {
            CouponsRepository.updateCoupon(this.couponsState.updateCoupon)
              .then((response) => {
                resolve({
                  body: this.translations.get(
                    "coupons_toastr_updated",
                    this.couponsState.updateCoupon.name
                  )
                });

                this.requestCoupons(
                  new ListRequest({
                    page: this.couponsState.page,
                    pageSize: this.couponsState.pageSize,
                    filters: this.couponsState.filters,
                    orders: [this.couponsState.orders]
                  })
                );

                this.resetState();
              })
              .catch((error) => {
                let message = "";
                if (error.response) {
                  message =
                    error.response.data.errorMessages[0].userVisibleMessage;
                }
                else {
                  message = this.translations.get("network_error");
                }
                reject({ body: message });
              });
          }),
        {
          timeout: 3000,
          closeOnClick: true
        }
      );
    }
    else {
      this.$snotify.simple(this.translations.get("toastr_nochanges"));
      this.resetState();
    }
  }

  /**
   * Method for submitting new coupon to api and refreshing the grid
   */
  submitNew() {
    if (this.couponsState.createCoupon.couponType === CouponType.Partner) {
      this.couponsState.createCoupon.expiryDate = null;
      this.couponsState.createCoupon.partnerId = this.couponsState.partner.id;
    }
    else {
      this.couponsState.createCoupon.partnerId = null;
    }

    // @ts-ignore
    this.$snotify.async(
      this.translations.get("coupons_toastr_adding"),
      () =>
        new Promise((resolve, reject) => {
          CouponsRepository.createCoupon(this.couponsState.createCoupon)
            .then(() => {
              resolve({
                body: this.translations.get(
                  "coupons_toastr_added",
                  this.couponsState.createCoupon.name
                )
              });

              this.requestCoupons(
                new ListRequest({
                  page: this.couponsState.page,
                  pageSize: this.couponsState.pageSize,
                  filters: this.couponsState.filters,
                  orders: [this.couponsState.orders]
                })
              );

              this.resetState();
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true
      }
    );
  }

  /**
   * Getter method to transform the list of coupons to grid data (vTable)
   */
  get transformCoupons() {
    return this.couponsState.coupons.map((element: Coupon) => {
      return {
        e1: element.code,
        e2: element.partnerId ? element.partner.name : "",
        e3: element.createdOn
          ? this.$moment(element.createdOn).format(dateTimeDisplayFormat)
          : "",
        e4: element.isActive
          ? {
            component: "div",
            props: { class: "label label-success text-center" },
            html: this.translations.get("active")
          }
          : {
            component: "div",
            props: { class: "label label-danger text-center" },
            html: this.translations.get("inactive")
          },
        e5:
          element.couponType === CouponType.Individual
            ? this.translations.get("coupons_type_individual")
            : this.translations.get("coupons_type_partner"),
        e6: element.expiryDate
          ? this.$moment(element.expiryDate).format(dateDisplayFormat) // element.expiryDate // this.$moment(this.testDate).format('YYYY')
          : this.translations.get("unlimited"),
        e7:
          element.discountType === DiscountType.Absolute
            ? this.translations.get("coupons_discounttype_absolute")
            : this.translations.get("coupons_discounttype_percentage"),
        e8: element.value,
        e9: element.usageLimit,
        e10: element.numberOfUsages,
        meta: {
          id: element.id,
          link: element.name
        }
      };
    });
  }

  /**
   * Method for fetching coupons based on list request
   *
   * @param {ListRequest} listRequest
   */
  requestCoupons(listRequest: ListRequest) {
    this.couponsState.tableLoading = true;
    CouponsRepository.fetchCoupons(listRequest)
      .then((response) => {
        this.couponsState.count = response.data.count;
        this.couponsState.totalCount = response.data.totalCount;
        this.couponsState.coupons = response.data.result;

        this.couponsState.tableLoading = false;
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else this.$snotify.error(this.translations.get("network-error"));
        this.couponsState.tableLoading = false;
      });
  }

  mounted() {
    let request: ListRequest = { ...this.$store.getters.getListRequest };
    this.requestCoupons(request);
    // Reset redirect info if redirected from Partners
    this.$store.dispatch(
      PARTNERS_REDIRECT,
      new ListRequest({ page: 1, pageSize: 25 })
    );

    PartnersRepository.fetchPartners(
      new ListRequest({ page: 1, pageSize: 999 })
    )
      .then((response) => {
        this.couponsState.partners = response.data.result;
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  created() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["coupons", "common"]
    });

    document.title = 'Coupons | MenuSano';
  }

  get translations() {
    return this.localizationState.translations;
  }
}
export default Coupons;
